import React from 'react';
import { useTranslation } from 'react-i18next';
import type { UpdaterStatus } from '@shared/types';
import { usePlatformServices } from '@/platform/context';
import happilyLogo from '../../assets/happily-logo.png';

interface Props {
  demoMode: boolean;
  isLoggedIn: boolean;
  canGoBack?: boolean;
  canGoForward?: boolean;
  onBack?: () => void;
  onForward?: () => void;
  updaterStatus?: UpdaterStatus;
  onUpdaterAction?: () => void;
}

type UpdaterTone = 'progress' | 'ready' | 'warning' | 'error';

function getUpdaterPresentation(status: UpdaterStatus | undefined, t: (key: string, options?: Record<string, unknown>) => string) {
  if (!status) return null;

  switch (status.state) {
    case 'checking':
      return {
        label: t('titlebar.updatesChecking'),
        tone: 'progress' as UpdaterTone,
        spinner: true,
        clickable: false,
      };
    case 'available':
    case 'downloading':
      // Labels come from the state, never from `notes` (raw English from the
      // updater). A progress-less download is the macOS installer unpacking it.
      return {
        label: status.progress != null
          ? t('titlebar.updatesDownloading', { progress: status.progress })
          : status.version
            ? t(status.state === 'available' ? 'titlebar.updatesAvailable' : 'titlebar.updatesPreparing', { version: status.version })
            : t('titlebar.updatesDownloadingNoProgress'),
        tone: 'progress' as UpdaterTone,
        spinner: true,
        clickable: false,
        progress: status.progress,
      };
    case 'downloaded':
      return {
        label: t('titlebar.updatesReady'),
        tone: 'ready' as UpdaterTone,
        spinner: false,
        clickable: true,
      };
    case 'auth-required':
    case 'error':
      return {
        label: t('titlebar.updatesRetry'),
        tone: 'error' as UpdaterTone,
        spinner: false,
        clickable: true,
      };
    default:
      return null;
  }
}

function UpdateStatusIcon({
  tone,
  spinner,
}: {
  tone: UpdaterTone;
  spinner: boolean;
}) {
  if (spinner) {
    return <span className="spinner inline-block w-3 h-3 border-[1.5px] border-current border-r-transparent rounded-full" />;
  }

  if (tone === 'ready') {
    return (
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 1.75v4.5" />
        <path d="m3.9 4.9 2.1 2.1 2.1-2.1" />
        <path d="M2.25 8.75h7.5" />
      </svg>
    );
  }

  if (tone === 'warning') {
    return (
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2.5" y="5.25" width="7" height="4.25" rx="1.1" />
        <path d="M4.25 5.25V4a1.75 1.75 0 1 1 3.5 0v1.25" />
      </svg>
    );
  }

  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.5 3.25V1.5L11 3l-1.5 1.5V2.75A3.75 3.75 0 0 0 2.96 5" />
      <path d="M2.5 8.75V10.5L1 9l1.5-1.5v1.75A3.75 3.75 0 0 0 9.04 7" />
    </svg>
  );
}

export default function Titlebar({
  demoMode,
  isLoggedIn,
  canGoBack,
  canGoForward,
  onBack,
  onForward,
  updaterStatus,
  onUpdaterAction,
}: Props) {
  const { t } = useTranslation('layout');
  const platformServices = usePlatformServices();
  const isMac = platformServices.os === 'darwin';
  const updaterPresentation = getUpdaterPresentation(updaterStatus, t);

  return (
    <div className="titlebar flex items-center justify-between px-4 border-b border-divider/50 bg-white">
      {/* Left side: padding for macOS traffic lights + logo */}
      <div className="flex items-center gap-2" style={{ paddingLeft: isMac ? 72 : 0 }}>
        <img src={happilyLogo} alt={t('titlebar.appName')} className="w-5 h-5" draggable={false} />
        <span className="text-name font-bold text-text-primary">{t('titlebar.appName')}</span>
        {demoMode && (
          <span className="text-tiny bg-warning/20 text-warning px-1.5 py-0.5 rounded font-bold">{t('titlebar.demo')}</span>
        )}
      </div>

      {/* Right side: nav buttons + window controls */}
      <div className="flex items-center gap-1">
        {updaterPresentation && (
          <button
            onClick={updaterPresentation.clickable ? onUpdaterAction : undefined}
            disabled={!updaterPresentation.clickable}
            className={`titlebar-update-pill ${
              updaterPresentation.clickable ? 'cursor-pointer' : 'cursor-default'
            }`}
            data-tone={updaterPresentation.tone}
            data-clickable={updaterPresentation.clickable ? 'true' : 'false'}
            title={updaterPresentation.label}
          >
            <span className="titlebar-update-pill__content">
              <span className="titlebar-update-pill__icon" aria-hidden="true">
                <UpdateStatusIcon
                  tone={updaterPresentation.tone}
                  spinner={updaterPresentation.spinner}
                />
              </span>
              <span className="titlebar-update-pill__label">{updaterPresentation.label}</span>
            </span>
            {typeof updaterPresentation.progress === 'number' && (
              <span className="titlebar-update-pill__progress" aria-hidden="true">
                <span
                  className="titlebar-update-pill__progress-fill"
                  style={{ width: `${Math.max(6, Math.min(100, updaterPresentation.progress))}%` }}
                />
              </span>
            )}
          </button>
        )}

        {isLoggedIn && onBack && onForward && (
          <div className="flex items-center gap-0.5 mr-1" data-no-drag>
            <button
              onClick={onBack}
              disabled={!canGoBack}
              className="w-7 h-7 rounded-md flex items-center justify-center text-text-secondary hover:bg-row-hover disabled:opacity-25 disabled:cursor-default transition-colors cursor-pointer bg-transparent border-none"
              title={t('titlebar.back')}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="8.5 3 4.5 7 8.5 11" />
              </svg>
            </button>
            <button
              onClick={onForward}
              disabled={!canGoForward}
              className="w-7 h-7 rounded-md flex items-center justify-center text-text-secondary hover:bg-row-hover disabled:opacity-25 disabled:cursor-default transition-colors cursor-pointer bg-transparent border-none"
              title={t('titlebar.forward')}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="5.5 3 9.5 7 5.5 11" />
              </svg>
            </button>
          </div>
        )}

        {!isMac && (
          <div className="flex items-center gap-0.5">
            <button
              onClick={() => platformServices.window.minimize()}
              className="w-8 h-8 rounded-md flex items-center justify-center text-text-secondary hover:bg-row-hover transition-colors cursor-pointer bg-transparent border-none"
              title={t('titlebar.minimize')}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                <rect y="5" width="12" height="1.5" rx="0.75" />
              </svg>
            </button>
            <button
              onClick={() => platformServices.window.maximize()}
              className="w-8 h-8 rounded-md flex items-center justify-center text-text-secondary hover:bg-row-hover transition-colors cursor-pointer bg-transparent border-none"
              title={t('titlebar.maximize')}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="1" y="1" width="10" height="10" rx="1" />
              </svg>
            </button>
            <button
              onClick={() => platformServices.window.close()}
              className="w-8 h-8 rounded-md flex items-center justify-center text-text-secondary hover:bg-coral hover:text-white transition-colors cursor-pointer bg-transparent border-none"
              title={t('titlebar.close')}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <line x1="2" y1="2" x2="10" y2="10" />
                <line x1="10" y1="2" x2="2" y2="10" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
